/**
 * Attach a lab report (F7.5-e). uc-core CreateLabReportDto (multipart) + LabReportService:
 *   files: pdf (required, the PDF as received, ≤ 20 MB), resultsFile (CSV / spreadsheet, optional)
 *   reportNo ≤ 40 · sampleId | sealPayload (≤ 64) · labOrgId? (defaults to the dispatch's lab)
 *   accreditationNo? ≤ 40 · testedAt (date) · receivedAt? · rows? JSON [{ panel, analyte, value, uom, loq, method }]
 *   used only when no results file. value: a number, or empty / ND / "<0.01" for not detected.
 * Verify (F7.5-f): { decision: VERIFIED | REJECTED, note? ≤ 300 } — a note is asked for a rejection.
 */

export const MAX_FILE_BYTES = 20 * 1024 * 1024

export interface LabRowDraft {
  panel: string
  analyte: string
  value: string
  uom: string
  loq: string
  method: string
}

export interface LabReportDraft {
  reportNo: string
  sampleId: string
  sealPayload: string
  labOrgId: string
  accreditationNo: string
  testedAt: string
  receivedAt: string
  pdf: File | null
  resultsFile: File | null
  rows: LabRowDraft[]
}

export interface LabReportProblems {
  reportNo?: string
  sample?: string
  accreditationNo?: string
  testedAt?: string
  receivedAt?: string
  pdf?: string
  resultsFile?: string
  rows?: string
  /** Per row index. */
  rowErrors?: Record<number, string>
}

export const emptyLabRow = (panel = ''): LabRowDraft => ({ panel, analyte: '', value: '', uom: '', loq: '', method: '' })

export const emptyLabReportDraft = (): LabReportDraft => ({
  reportNo: '',
  sampleId: '',
  sealPayload: '',
  labOrgId: '',
  accreditationNo: '',
  testedAt: '',
  receivedAt: '',
  pdf: null,
  resultsFile: null,
  rows: [emptyLabRow()],
})

const DECIMAL = /^\d+(\.\d+)?$/
const DATE = /^\d{4}-\d{2}-\d{2}$/
const ND_WORDS = new Set(['', 'ND', 'N.D.', 'NOT DETECTED', 'BLQ', 'BDL', '<LOQ', '< LOQ', 'BELOW LOQ'])

/** A typed result reads as "not detected" (sent as written; the server turns it into null). */
export function isNotDetected(value: string): boolean {
  const v = value.trim().toUpperCase()
  return ND_WORDS.has(v) || v.startsWith('<')
}

const blankRow = (r: LabRowDraft) => !r.panel.trim() && !r.analyte.trim() && !r.value.trim() && !r.uom.trim() && !r.loq.trim()

/** Rows that will be sent (blank rows are dropped). */
export const filledRows = (rows: LabRowDraft[]) => rows.filter((r) => !blankRow(r))

function rowProblem(r: LabRowDraft): string | null {
  const missing: string[] = []
  if (!r.panel.trim()) missing.push('test')
  if (!r.analyte.trim()) missing.push('substance')
  if (!r.uom.trim()) missing.push('unit')
  if (!r.loq.trim()) missing.push('LOQ')
  if (missing.length) return `Fill in ${missing.join(', ')}`
  if (!DECIMAL.test(r.loq.trim())) return 'LOQ must be a number, e.g. 0.01'
  if (!isNotDetected(r.value) && !DECIMAL.test(r.value.trim())) return 'Result must be a number, or ND / <LOQ for not detected'
  return null
}

const isPdf = (f: File) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
const isSheet = (f: File) => /\.(csv|xlsx|xls|ods)$/i.test(f.name) || /csv|spreadsheet|excel/.test(f.type)

export function validateLabReport(d: LabReportDraft, today: string): LabReportProblems {
  const p: LabReportProblems = {}
  const no = d.reportNo.trim()
  if (!no) p.reportNo = 'Enter the report number'
  else if (no.length > 40) p.reportNo = 'At most 40 characters'
  if (!d.sampleId && !d.sealPayload.trim()) p.sample = 'Scan or type the sample seal'
  else if (d.sealPayload.trim().length > 64) p.sample = 'At most 64 characters'
  if (d.accreditationNo.trim().length > 40) p.accreditationNo = 'At most 40 characters'
  if (!d.testedAt) p.testedAt = 'Pick the test date'
  else if (!DATE.test(d.testedAt)) p.testedAt = 'Not a date'
  else if (d.testedAt > today) p.testedAt = 'The test date cannot be in the future'
  if (d.receivedAt) {
    const day = d.receivedAt.slice(0, 10)
    if (!DATE.test(day)) p.receivedAt = 'Not a date'
    else if (d.testedAt && day < d.testedAt) p.receivedAt = 'Received before it was tested?'
    else if (day > today) p.receivedAt = 'Cannot be in the future'
  }
  if (!d.pdf) p.pdf = 'Attach the report PDF as received'
  else if (!isPdf(d.pdf)) p.pdf = 'The report must be a PDF file, not a photo'
  else if (d.pdf.size > MAX_FILE_BYTES) p.pdf = 'At most 20 MB'
  if (d.resultsFile) {
    if (!isSheet(d.resultsFile)) p.resultsFile = 'A CSV or spreadsheet file'
    else if (d.resultsFile.size > MAX_FILE_BYTES) p.resultsFile = 'At most 20 MB'
  } else {
    const rows = filledRows(d.rows)
    if (rows.length === 0) p.rows = 'Attach the results file or type at least one row'
    const rowErrors: Record<number, string> = {}
    const seen = new Set<string>()
    d.rows.forEach((r, i) => {
      if (blankRow(r)) return
      const prob = rowProblem(r)
      if (prob) {
        rowErrors[i] = prob
        return
      }
      const key = `${r.panel.trim().toUpperCase()}|${r.analyte.trim().toUpperCase()}`
      if (seen.has(key)) rowErrors[i] = 'This test and substance appear twice'
      seen.add(key)
    })
    if (Object.keys(rowErrors).length) {
      p.rowErrors = rowErrors
      p.rows ??= 'Fix the rows marked below'
    }
  }
  return p
}

export const labReportOk = (p: LabReportProblems) =>
  Object.entries(p).every(([k, v]) => (k === 'rowErrors' ? !v || Object.keys(v).length === 0 : !v))

/** Text fields of the multipart body (files are added by the caller). Call only when valid. */
export function labReportFields(d: LabReportDraft): Record<string, string> {
  const out: Record<string, string> = { reportNo: d.reportNo.trim(), testedAt: d.testedAt }
  if (d.sampleId) out.sampleId = d.sampleId
  else out.sealPayload = d.sealPayload.trim()
  if (d.labOrgId) out.labOrgId = d.labOrgId
  if (d.accreditationNo.trim()) out.accreditationNo = d.accreditationNo.trim()
  if (d.receivedAt) out.receivedAt = new Date(d.receivedAt).toISOString()
  if (!d.resultsFile) {
    out.rows = JSON.stringify(
      filledRows(d.rows).map((r) => ({
        panel: r.panel.trim().toUpperCase(),
        analyte: r.analyte.trim(),
        value: isNotDetected(r.value) ? null : r.value.trim(),
        uom: r.uom.trim(),
        loq: r.loq.trim(),
        ...(r.method.trim() ? { method: r.method.trim() } : {}),
      })),
    )
  }
  return out
}

/** The multipart body for POST /lab-reports. */
export function labReportFormData(d: LabReportDraft): FormData {
  const fd = new FormData()
  for (const [k, v] of Object.entries(labReportFields(d))) fd.append(k, v)
  if (d.pdf) fd.append('pdf', d.pdf, d.pdf.name)
  if (d.resultsFile) fd.append('resultsFile', d.resultsFile, d.resultsFile.name)
  return fd
}

// ---------------- verify ----------------

export interface VerifyDraft {
  decision: 'VERIFIED' | 'REJECTED' | ''
  note: string
}
export const SAME_PERSON_REASON = 'You entered this report; another person must check it.'

export function validateVerify(d: VerifyDraft): { decision?: string; note?: string } {
  const p: { decision?: string; note?: string } = {}
  if (!d.decision) p.decision = 'Choose Verified or Reject'
  const note = d.note.trim()
  if (d.decision === 'REJECTED' && !note) p.note = 'Say what is wrong with the rows'
  else if (note.length > 300) p.note = 'At most 300 characters'
  return p
}

/** Why the signed-in person may not verify this report, or null. */
export function verifyBlockedReason(userId: string | null | undefined, report: { enteredById: string | null; status: string }): string | null {
  if (userId && report.enteredById && userId === report.enteredById) return SAME_PERSON_REASON
  if (report.status !== 'ENTERED') return 'Only a report waiting for its check can be verified or rejected.'
  return null
}

// ---------------- server refusals ----------------

const FIELD_WORDS: Array<[RegExp, string]> = [
  [/\bsampleId or sealPayload\b/, 'Sample seal'],
  [/\blabOrgId\b/, 'Lab'],
  [/\baccreditationNo\b/, 'Accreditation number'],
  [/^pdf\b/, 'Report PDF'],
  [/\brows \(results file or typed rows\)/, 'Results (a results file or typed rows)'],
  [/\.panel\b/, ' test'],
  [/\.analyte\b/, ' substance'],
  [/\.uom\b/, ' unit'],
  [/\.loq\b/, ' LOQ'],
  [/\.value\b/, ' result'],
]

/** A REPORT_INCOMPLETE `missing` line, readable: "rows[0].loq must be a number" → "Row 1 LOQ must be a number". */
export function readableMissing(line: string): string {
  let s = line.replace(/\brows\[(\d+)\]/g, (_m, n: string) => `Row ${Number(n) + 1}`)
  for (const [re, words] of FIELD_WORDS) s = s.replace(re, words)
  return s.charAt(0).toUpperCase() + s.slice(1)
}
